import React, { useMemo } from 'react'
import { View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSelector } from 'react-redux'
import { Feature, NavigationBarAccessory, useFeatureFlag } from '@components'
import { Analytics, CustomEventTypes } from '@src/config'
import { Icon, Text } from '@components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import { RootStackParamList } from '@src/navigation/types'
import { notificationInboxUnreadCountSelector } from '@src/selectors/notifications'

const MAX_BADGE_COUNT = 99

export const useNotificationInboxNavigationAccessory = () => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()
  const notificationInboxEnabled = useFeatureFlag(Feature.NotificationInbox)
  const unreadCount = useSelector(notificationInboxUnreadCountSelector)

  return useMemo<NavigationBarAccessory[]>(() => {
    if (!notificationInboxEnabled) {
      return []
    }

    let badgeLabel: string | null = null
    if (unreadCount > MAX_BADGE_COUNT) {
      badgeLabel = `${MAX_BADGE_COUNT}+`
    } else if (unreadCount > 0) {
      badgeLabel = unreadCount.toString()
    }

    return [
      {
        renderIconComponent: () => (
          <View style={styles.iconContainer}>
            <Icon name="bell" style={styles.icon} />
            {badgeLabel && (
              <View style={styles.badge}>
                <Text type="tiny" bold style={styles.badgeText}>
                  {badgeLabel}
                </Text>
              </View>
            )}
          </View>
        ),
        onPress: () => {
          Analytics.track(CustomEventTypes.NotificationInboxOpened, { unreadCount })
          navigation.navigate('NotificationInbox')
        },
        accessibilityLabel: badgeLabel ? `Notifications, ${badgeLabel} unread` : 'Notifications',
      },
    ]
  }, [
    navigation,
    notificationInboxEnabled,
    styles.badge,
    styles.badgeText,
    styles.icon,
    styles.iconContainer,
    unreadCount,
  ])
}

const themedStyles = StyleService.create({
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 40,
    width: 44,
  },
  icon: {
    color: 'theme.text.primary',
  },
  badge: {
    position: 'absolute',
    top: 3,
    right: 4,
    minWidth: 19,
    height: 19,
    borderRadius: 9.5,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'theme.error.base',
  },
  badgeText: {
    color: 'theme.solid.white',
  },
})
